// @ts-nocheck
import {
  apply,
  first,
  group_by,
  map,
  merge,
  object_map,
  scan,
} from "@pdfking/rfunc"
import { Identity, JSONDecode } from "@pdfking/tfunc"
import uuid from "@pdfking/workflow/utils/uuid"
import autocomplete from "../comp/autocomplete"
import barcode from "../comp/barcode"
import cart_counter from "../comp/cart_counter"
import collapsable from "../comp/collapsable"
import double from "../comp/double"
import double_autocomplete from "../comp/double_autocomplete"
import markdown from "../comp/markdown"
import megamenu from "../comp/megamenu"
import messages from "../comp/messages"
import publicsearch from "../comp/publicsearch"
import rating from "../comp/rating"
import routerview from "../comp/routerview"
import scroll from "../comp/scroll"
import searchselect from "../comp/searchselect"
import sizedatalist from "../comp/sizedatalist"
import uploader from "../comp/uploader"
import username from "../comp/username"
import autocomplete_index from "../constants/autocomplete"
import append from "../dom/append"
import element from "../dom/element"
import selector from "../dom/selector"
import register_dom_update from "../navigation/register_dom_update"
import navigate from "../routes/navigate"
import { SLIDESHOW_VIEW } from "../routes/urls"
import user from "../state/user"
import init from "./init"

function require_staff(func, func2) {
  return el => {
    if (user.is_staff) {
      return (func || Identity)(el)
    }
    return (func2 || Identity)(el)
  }
}

const components = {
  autocomplete: el =>
    init(autocomplete, el, {
      multiple: el.matches("[multiple]"),
      options: {},
      value:
        JSONDecode(el.getAttribute("data-initial") || "null") ||
        (el.value ? [{ id: el.value, unicode: el.value }] : []),
    }),
  autocomplete_select: el =>
    autocomplete_index
      .load_autocomplete(el.getAttribute("data-autocomplete"))
      .then(content => {
        const options = {}

        scan(
          labels =>
            scan(
              (unicode, id) =>
                (options[id] = {
                  group: labels.name,
                  id,
                  unicode,
                }),
              labels.objects
            ),
          content ? content.objects : []
        )

        return init(searchselect, el, {
          options,
          value: el.value,
        })
      }),
  barcode: el => init(barcode, el, { name: el.name, value: el.value }),

  cart_counter: el => init(cart_counter, el),

  collapsable: el => init(collapsable, el, { preserve_class: true }),

  double: el =>
    init(double, el, {
      options: group_by(
        map(
          el =>
            apply(
              (f, s) => ({
                group: s ? f : f,
                label: el.parentNode.getAttribute("label") || "",
                name: s ? s : "",
                value: el.value,
              }),
              el.innerHTML.split(": ")
            ),
          selector("option", el)
        ),
        info => info.label,
        array =>
          group_by(
            array,
            info => info.group,
            array =>
              object_map(
                info => info.value,
                info => info.name,
                array
              )
          )
      ),
      size: "70%",
    }),

  double_autocomplete: el => init(double_autocomplete, el),
  filelist: el =>
    import(
      /* webpackChunkName: "history" */ "@pdfking/frontend/comp/files/filelist"
    ).then(m =>
      init(m.default, el, {
        filelist: el.getAttribute("data-filelist"),
      })
    ),
  imageload: el => {
    const strip = () => {
      el.removeAttribute("loading")
    }

    if (el.complete) {
      strip()
    } else {
      el.addEventListener("load", strip)
    }
  },
  markdown: require_staff(el =>
    init(markdown, el, { placeholder: el.getAttribute("placeholder") })
  ),

  megamenu: el => megamenu(el),
  publicsearch: el => init(publicsearch, el),
  rating: el => init(rating, el),
  report: require_staff(el =>
    import(
      /* webpackChunkName: "report" */ "@pdfking/frontend/comp/report"
    ).then(m =>
      init(m.default, el, {
        report: JSONDecode(first(selector("script", el)).innerHTML),
      })
    )
  ),
  routerview: el => init(routerview, el),
  scroll: el => scroll(el),
  searchselect: el =>
    init(searchselect, el, {
      multiple: el.matches("[multiple]"),
      options: object_map(
        opt => opt.value,
        opt => ({
          group: opt.parentNode.getAttribute("label") || null,
          id: opt.value,
          unicode: opt.innerHTML,
        }),
        selector("option", el)
      ),
      value: map(opt => opt.value, selector("option[selected]", el)),
    }),

  sizedatalist: el => {
    const component = element("div")
    const id = uuid()

    el.setAttribute("list", id)

    append(el.parentNode, component)

    init(
      sizedatalist,
      component,
      merge(
        object_map(
          k => k,
          k => JSONDecode(el.getAttribute(`data-${k}`) || "[]"),
          ["units", "min", "max"]
        ),
        { autocomplete: el.getAttribute("data-autocomplete"), id }
      )
    )
  },

  slideshow: el => {
    if (!el.getAttribute("href"))
      el.addEventListener("click", e => {
        e.preventDefault()
        e.stopPropagation()

        navigate(SLIDESHOW_VIEW, {
          selected: el.getAttribute("data-slide-src") || el.getAttribute("src"),
          selector: el.getAttribute("data-selector"),
        })
      })
  },

  spreadsheet: el =>
    import(
      /* webpackChunkName: "spreadsheet" */ "@pdfking/frontend/comp/spreadsheet"
    ).then(m =>
      init(m.default, el, {
        errors: JSONDecode(el.getAttribute("data-errors")),
        input: JSONDecode(el.getAttribute("value") || "[{}]"),
        name: el.getAttribute("name"),
        schema: JSONDecode(el.getAttribute("data-schema")),
      })
    ),

  toggleclass: el => {
    const classname = el.getAttribute("data-class")
    const target = el.getAttribute("data-target")
    el.addEventListener("click", e =>
      scan(
        e =>
          e.classList.contains(classname)
            ? e.classList.remove(classname)
            : e.classList.add(classname),
        selector(target)
      )
    )
  },
  uploader: async el =>
    init(uploader, el, {
      filelist: el.getAttribute("data-filelist"),
    }),
  username: el => init(username, el),
}

init(messages)

register_dom_update({
  after: () => {
    scan(el => {
      const func = components[el.getAttribute("data-component")]
      if (func) func(el)
      el.removeAttribute("data-component")
    }, selector("[data-component]"))

    scan(components.searchselect, selector("select[multiple]"))
    scan(components.imageload, selector("[loading=lazy]"))
  },
  login: () =>
    user.is_staff
      ? import(
          /* webpackChunkName: "admin" */ "@pdfking/frontend/comp/files/filesink"
        ).then(m => init(m.default))
      : null,
})
